import { useEffect, useState } from 'react';
import api from '../services/api';
import PermissionsContainer from './PermissionsContainer';
import { PermissionAwareButton, useComponentAccessLevel } from '../contexts/PermissionAccessContext';

type Source = { path: string; line: number; excerpt: string };
type Message = { role: 'user' | 'assistant'; content: string; sources?: Source[] };

function Conversation() {
  const canEdit = (useComponentAccessLevel() ?? 0) >= 2;
  const [conversationId, setConversationId] = useState<string>();
  const [choosing, setChoosing] = useState(true);
  const [recent, setRecent] = useState<{ id: string; title: string }[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!canEdit) { setLoadingRecent(false); return; }
    let active = true;
    api.get('/smart-dispatch/ask/conversations').then(response => {
      if (active) setRecent(response.data);
    }).catch(() => { if (active) setError('Unable to load previous conversations.'); })
      .finally(() => { if (active) setLoadingRecent(false); });
    return () => { active = false; };
  }, [canEdit]);
  async function resume(id: string) {
    if (busy || !canEdit) return;
    setBusy(true); setError('');
    try {
      const response = await api.get(`/smart-dispatch/ask/conversations/${id}`);
      setMessages(response.data.messages); setConversationId(id); setChoosing(false);
    } catch { setError('Unable to open this conversation.'); }
    finally { setBusy(false); }
  }
  async function ask(event: React.FormEvent) {
    event.preventDefault();
    if (!question.trim() || busy || !canEdit) return;
    setBusy(true);
    setError('');
    const text = question.trim();
    try {
      const response = await api.post('/smart-dispatch/ask', {
        question: text, conversation_id: conversationId,
      }, { timeout: 150000 });
      setMessages(response.data.messages);
      setConversationId(response.data.id);
      setQuestion('');
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to answer right now. Please try again.');
    } finally { setBusy(false); }
  }
  if (choosing) return <div className="space-y-4">
    <p className="text-sm text-muted">Resume one of your last five conversations or start a fresh one.</p>
    {loadingRecent && <p role="status">Loading conversations…</p>}
    {error && <p role="alert">{error}</p>}
    {recent.map(item => <PermissionAwareButton key={item.id} disabled={busy} onClick={() => void resume(item.id)} className="block w-full rounded-lg border border-default p-3 text-left text-heading">{item.title}</PermissionAwareButton>)}
    <PermissionAwareButton disabled={busy} onClick={() => { setChoosing(false); setError(''); }} className="rounded-lg bg-accent px-4 py-2 text-accent-fg">Start a new conversation</PermissionAwareButton>
  </div>;
  return <div className="space-y-4">
    <p className="text-sm text-muted">Ask about planning rules, strategies, or eligibility. Answers use the current planner code; they don’t inspect live loads or change your plan.</p>
    <div aria-live="polite" className="space-y-4">
      {messages.map((message, index) => <div key={index} className="rounded-lg border border-default p-3">
        <p className="text-sm font-semibold text-heading">{message.role === 'user' ? 'You' : 'Smart Preplan'}</p>
        <p className="whitespace-pre-wrap text-sm text-body mt-2">{message.content}</p>
        {!!message.sources?.length && <details className="mt-3 text-xs text-muted"><summary>Code references</summary>
          {message.sources.map(source => <details key={`${source.path}:${source.line}`} className="mt-2">
            <summary>{source.path}:{source.line}</summary><pre className="overflow-x-auto max-h-64 whitespace-pre text-xs p-2">{source.excerpt}</pre>
          </details>)}
        </details>}
      </div>)}
      {busy && <p role="status" className="text-sm text-muted">Reading the planner code…</p>}
    </div>
    <form onSubmit={ask} className="space-y-3">
      <label className="block text-sm font-medium text-heading" htmlFor="smart-preplan-question">Your question</label>
      <textarea id="smart-preplan-question" value={question} onChange={event => setQuestion(event.target.value)} maxLength={2000} rows={3}
        disabled={busy || !canEdit} placeholder="For example: Does Smart Preplan plan domestic moves?"
        className="w-full rounded-lg border border-default bg-panel p-3 text-body disabled:opacity-50" />
      {error && <p role="alert" className="text-sm text-danger">{error}</p>}
      <PermissionAwareButton type="submit" disabled={busy || !question.trim()} className="rounded-lg bg-accent px-4 py-2 text-accent-fg disabled:opacity-50">Ask Smart Preplan</PermissionAwareButton>
    </form>
  </div>;
}

export default function AskSmartPreplan() {
  return <PermissionsContainer componentId="smart-preplan-ask" componentName="Ask Smart Preplan" defaultHidden>
    <Conversation />
  </PermissionsContainer>;
}
